import { useEffect, useMemo, useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { formatDate, todayLocal } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type {
  Candidate,
  CandidateDetail,
  CoreValue,
  RecruitingFramework,
  RecruitingQuestion,
  RecruitingRole,
} from '../../lib/types';
import {
  CombinedDialog,
  CompetencyGuide,
  StageHeader,
  StagePicker,
  WeightedScorecard,
} from './RecruitingStages';
import { exportScorecardPdf } from './scorecardReport';
import {
  BANDS,
  SCALE,
  WEIGHTS,
  bandFor,
  bandRange,
  breakdown,
  criticalConcerns,
  ratingClass,
  weightedOutcome,
  type RefScoreRow,
  type ScoreRow,
} from './scoring';

/**
 * Recruiting — an interview question bank and scoring rubric per role.
 *
 * Roles come from the training matrix, which already mirrors People Templates,
 * so anything defined once in the module appears here.
 *
 * Each question carries a category, a weight and guidance describing what a
 * strong answer sounds like — the guidance is the part that makes two
 * interviewers score the same answer alike. Unrated questions are excluded
 * from the total, so a partial interview still scores fairly.
 */
export function Recruiting({ onChanged }: { onChanged?: () => void }) {
  const roles = useResource<RecruitingRole[]>('/people/recruiting/roles');
  const coreValues = useResource<CoreValue[]>('/people/recruiting/core-values');
  const [roleId, setRoleId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showScale, setShowScale] = useState(false);

  const list = roles.data ?? [];
  /* Keep the current selection if it still exists, otherwise fall back to the
     first role — a role can be renamed or deleted from the training matrix
     while this tab is open. */
  const role = list.find((r) => r.TrainingRoleId === roleId) ?? list[0] ?? null;

  const questions = useResource<RecruitingQuestion[]>(
    role ? `/people/recruiting/questions?roleId=${role.TrainingRoleId}` : null,
  );
  const candidates = useResource<Candidate[]>(
    role ? `/people/recruiting/candidates?roleId=${role.TrainingRoleId}` : null,
  );

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      roles.reload();
      questions.reload();
      candidates.reload();
      onChanged?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setBusy(false);
    }
  };

  if (roles.loading) return <Loading label="Loading roles" />;
  if (roles.error) return <ErrorBanner message={roles.error} />;

  const anyEmpty = list.some((r) => r.QuestionCount === 0);
  const anyMissingValues = list.some(
    (r) => r.QuestionCount > 0 && r.CoreValueCount < r.CoreValuesTotal,
  );

  return (
    <>
      <div className="row-between wrap mb-4">
        <p className="muted small" style={{ maxWidth: '60ch' }}>
          Score every candidate the same way. Answers are rated 1–5 against shared anchors, weighted
          by how much the question matters, and totalled against a hire / borderline / no band.
        </p>
        <div className="row wrap">
          <button type="button" className="btn btn-ghost" onClick={() => setShowScale((s) => !s)}>
            {showScale ? 'Hide' : 'Rating'} scale
          </button>
          {anyMissingValues && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy}
              onClick={() => void run(() => api.post('/people/recruiting/core-values-all'))}
            >
              Add core values questions
            </button>
          )}
          {anyEmpty && (
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() => void run(() => api.post('/people/recruiting/sample-all'))}
            >
              {busy ? 'Loading…' : 'Load samples for all roles'}
            </button>
          )}
        </div>
      </div>

      {error && <ErrorBanner message={error} />}

      {showScale && <ScalePanel coreValues={coreValues.data ?? []} />}

      {list.length === 0 ? (
        <Empty title="No roles defined yet">
          <p className="muted">
            Roles come from the training matrix. Create a People Template, or add a role there
            first.
          </p>
        </Empty>
      ) : (
        <div className="grid grid-2" style={{ gridTemplateColumns: 'minmax(200px, 260px) 1fr' }}>
          <nav className="stack" style={{ gap: 'var(--space-2)' }}>
            {list.map((r) => (
              <button
                key={r.TrainingRoleId}
                type="button"
                className={`btn rec-role ${r.TrainingRoleId === role?.TrainingRoleId ? 'btn-secondary' : 'btn-ghost'}`}
                onClick={() => setRoleId(r.TrainingRoleId)}
              >
                <span className="rec-role-name">{r.Name}</span>
                <span className="rec-role-count xs">{r.QuestionCount}</span>
              </button>
            ))}
          </nav>

          <div>
            {role && (
              <RoleBank
                role={role}
                questions={questions.data ?? []}
                loading={questions.loading}
                candidates={candidates.data ?? []}
                coreValues={coreValues.data ?? []}
                busy={busy}
                onRun={run}
              />
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ScalePanel({ coreValues }: { coreValues: CoreValue[] }) {
  return (
    <section className="card mb-4">
      <div className="card-head">
        <div className="grow">
          <h3>Rating scale</h3>
          <div className="sub">
            Shared by every role, so a "4" means the same thing across the business — the
            per-question guidance is what makes it role-specific.
          </div>
        </div>
      </div>
      <div className="card-body">
        <div className="grid grid-4">
          {SCALE.map((s) => (
            <div key={s.value} className="stat">
              <div className="label">
                {s.value} · {s.label}
              </div>
              <div className="foot">{s.blurb}</div>
            </div>
          ))}
        </div>
        <p className="xs muted mt-4">
          Each question carries a weight — <strong>Critical</strong> counts triple a{' '}
          <strong>Nice to have</strong>, so a weak answer where it matters costs more than a weak
          answer where it does not.
        </p>

        {coreValues.length > 0 && (
          <>
            <h4 className="mt-6">Core values</h4>
            <p className="xs muted">
              Asked for every role, because a value that only applied to some roles would not be
              one.
            </p>
            <div className="grid grid-4 mt-2">
              {coreValues.map((v) => (
                <div key={v.name} className="stat">
                  <div className="label">{v.name}</div>
                  <div className="foot">{v.meaning}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/* =========================================================================
   One role's question bank, and scoring against it
   ========================================================================= */

function RoleBank({
  role,
  questions,
  loading,
  candidates,
  coreValues,
  busy,
  onRun,
}: {
  role: RecruitingRole;
  questions: RecruitingQuestion[];
  loading: boolean;
  candidates: Candidate[];
  coreValues: CoreValue[];
  busy: boolean;
  onRun: (fn: () => Promise<unknown>) => Promise<void>;
}) {
  const [scoring, setScoring] = useState(false);
  const [scores, setScores] = useState<Record<number, number>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [editing, setEditing] = useState<RecruitingQuestion | null>(null);
  const [adding, setAdding] = useState(false);
  const [savingCard, setSavingCard] = useState(false);
  const [viewing, setViewing] = useState<number | null>(null);
  const [stageKey, setStageKey] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const [combining, setCombining] = useState<string | null>(null);

  /* The stages, the competency model and the candidate brief. A role that
     defines none of them comes back with empty arrays and everything below
     falls through to the flat question bank — which is what every role that
     predates staged interviews does. */
  const framework = useResource<RecruitingFramework>(
    `/people/recruiting/roles/${role.TrainingRoleId}/framework`,
  );
  const stages = useMemo(() => framework.data?.stages ?? [], [framework.data]);
  const competencies = useMemo(() => framework.data?.competencies ?? [], [framework.data]);
  const staged = stages.length > 0;
  const activeStage = stages.find((x) => x.StageKey === stageKey) ?? null;

  /* A score belongs to the candidate being interviewed, not the role — moving
     to another role mid-scorecard would silently blend two people's ratings.
     The evidence written against those scores has to go with them. */
  useEffect(() => {
    setScores({});
    setComments({});
    setStageKey(null);
  }, [role.TrainingRoleId]);

  /* Land on the first part rather than on nothing: an interviewer arriving
     here is about to run a stage, and an empty screen asking them to choose
     is a step with one sensible answer. */
  useEffect(() => {
    if (staged && stageKey === null) setStageKey(stages[0]!.StageKey);
  }, [staged, stageKey, stages]);

  const meanings = useMemo(
    () => Object.fromEntries(coreValues.map((v) => [v.name, v.meaning])),
    [coreValues],
  );

  /* Everything below works on the questions IN PLAY, not on the role's whole
     bank: a scorecard belongs to one interviewer running one stage, and a
     total spanning questions they never asked would be nobody's number. */
  const inPlay = useMemo(
    () => (staged ? questions.filter((q) => q.StageKey === stageKey) : questions),
    [staged, questions, stageKey],
  );

  /* Saved scorecards grouped by the person they are about. Matched on the
     trimmed, lower-cased name because that is what the API matches on, and a
     group the two disagreed about would offer a button that then 404s.
     Only people with more than one card: "combining" a single scorecard is
     just that scorecard. */
  const byCandidate = useMemo(() => {
    const g = new Map<string, { key: string; name: string; cards: Candidate[]; stages: string[] }>();
    for (const c of candidates) {
      const key = c.Name.trim().toLowerCase();
      const found = g.get(key) ?? { key, name: c.Name.trim(), cards: [], stages: [] };
      found.cards.push(c);
      if (c.StageName && !found.stages.includes(c.StageName)) found.stages.push(c.StageName);
      g.set(key, found);
    }
    return [...g.values()].filter((x) => x.cards.length > 1);
  }, [candidates]);

  const stageCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const q of questions) if (q.StageKey) c[q.StageKey] = (c[q.StageKey] ?? 0) + 1;
    return c;
  }, [questions]);

  const grouped = useMemo(() => {
    const g = new Map<string, RecruitingQuestion[]>();
    for (const q of inPlay) g.set(q.Category, [...(g.get(q.Category) ?? []), q]);
    return [...g.entries()];
  }, [inPlay]);

  const result = useMemo(() => {
    const rated = inPlay.filter((q) => scores[q.RecruitingQuestionId]);
    if (rated.length === 0) return null;
    const got = rated.reduce((n, q) => n + scores[q.RecruitingQuestionId]! * q.Weight, 0);
    const max = rated.reduce((n, q) => n + 5 * q.Weight, 0);
    const pct = Math.round((got / max) * 100);
    const rows: ScoreRow[] = rated.map((q) => ({
      category: q.Category,
      weight: q.Weight,
      rating: scores[q.RecruitingQuestionId]!,
      text: q.Text,
    }));
    return { rated: rated.length, total: inPlay.length, got, max, pct, band: bandFor(pct), rows };
  }, [inPlay, scores]);

  /* The same ratings read the way the decision is actually made: by
     competency, weighted by each one's share of the hire, with the hard gates
     checked separately. Only for a role that defines a model — without one
     there is nothing to weight against and the percentage band above is the
     honest answer. */
  const outcome = useMemo(() => {
    if (competencies.length === 0) return null;
    const rows: RefScoreRow[] = inPlay
      .filter((q) => scores[q.RecruitingQuestionId])
      .map((q) => ({
        ref: q.CompetencyRef,
        weight: q.Weight,
        rating: scores[q.RecruitingQuestionId]!,
      }));
    return weightedOutcome(
      rows,
      competencies.map((c) => ({
        ref: c.Ref,
        name: c.Name,
        weightPct: c.WeightPct,
        isGate: c.IsGate,
      })),
    );
  }, [competencies, inPlay, scores]);

  if (loading) return <Loading label="Loading questions" />;

  if (questions.length === 0) {
    return (
      <>
        <Empty title={`No questions for ${role.Name} yet`}>
          <p className="muted">
            {role.SampleMatched ? (
              <>
                A sample set is available: <strong>{role.SampleLabel}</strong>.
              </>
            ) : (
              <>
                No role-specific set matches this name, so the <strong>general</strong> bank will be
                used.
              </>
            )}
          </p>
          <div className="row mt-4" style={{ justifyContent: 'center' }}>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() =>
                void onRun(() => api.post(`/people/recruiting/roles/${role.TrainingRoleId}/sample`))
              }
            >
              Load sample questions
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
              Add your own
            </button>
          </div>
        </Empty>

        {adding && (
          <QuestionDialog
            question={null}
            roleId={role.TrainingRoleId}
            onClose={() => setAdding(false)}
            onDone={() => {
              setAdding(false);
              void onRun(async () => undefined);
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="row-between wrap mb-4">
        <div>
          <h3>{role.Name}</h3>
          <p className="xs subtle">
            {staged
              ? `${questions.length} questions across ${stages.length} parts`
              : `${questions.length} questions across ${grouped.length} categories`}
            {role.CoreValueCount > 0 &&
              ` · ${role.CoreValueCount} of ${role.CoreValuesTotal} core values covered`}
          </p>
        </div>
        <div className="row wrap">
          {/* Reference rather than instruction: read once before the
              interview, checked again at the debrief, in the way for the hour
              in between. */}
          {competencies.length > 0 && (
            <button type="button" className="btn btn-ghost" onClick={() => setShowGuide(true)}>
              Competencies
            </button>
          )}
          <button
            type="button"
            className={scoring ? 'btn btn-secondary' : 'btn btn-ghost'}
            onClick={() => setScoring((s) => !s)}
          >
            {scoring ? 'Scoring' : 'Score a candidate'}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setAdding(true)}>
            Add question
          </button>
        </div>
      </div>

      {staged && (
        <StagePicker
          stages={stages}
          counts={stageCounts}
          active={stageKey}
          onPick={(k) => {
            /* Ratings belong to the stage they were given in. Carrying them
               across would let a panel score land on the executive final. */
            setScores({});
            setComments({});
            setStageKey(k);
          }}
        />
      )}

      {staged && activeStage && (
        <StageHeader
          stage={activeStage}
          brief={framework.data?.brief ?? null}
          roleName={role.Name}
        />
      )}

      {scoring && (
        <section className="card mb-4">
          <div className="card-body">
            {result ? (
              <>
                <div className="row-between wrap">
                  <div className="row wrap">
                    {/* A role with a competency model is decided on the
                        weighted 1-5 and its gates, not on the percentage —
                        so that is what the headline says. */}
                    {outcome && outcome.weighted !== null ? (
                      <span className={`badge ${outcome.decision.badge}`}>
                        {outcome.weighted.toFixed(2)} · {outcome.decision.label}
                      </span>
                    ) : (
                      <span className={`badge ${result.band.badge}`}>
                        {result.pct}% · {result.band.label}
                      </span>
                    )}
                    <span className="xs muted">
                      {result.got} of {result.max} weighted points · {result.rated} of{' '}
                      {result.total} questions rated
                    </span>
                  </div>
                  <div className="row wrap">
                    {/* Collapsed by default: the full table would push the
                        questions it is summarising off the screen. */}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowBreakdown((b) => !b)}
                    >
                      {showBreakdown ? 'Hide' : 'Show'} breakdown
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setSavingCard(true)}>
                      Save scorecard
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => {
                        setScores({});
                        setComments({});
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {showBreakdown && (
                  <div className="stack mt-4">
                    <CriticalConcerns rows={result.rows} />
                    {/* For a role with a competency model this IS the result;
                        the percentage band above is the same ratings read a
                        cruder way, kept because it is what every other role
                        shows and the two must not silently disagree. */}
                    {outcome ? (
                      <WeightedScorecard outcome={outcome} />
                    ) : (
                      <>
                        <ScoreBreakdown rows={result.rows} />
                        <DecisionThresholds pct={result.pct} />
                      </>
                    )}
                  </div>
                )}
              </>
            ) : (
              <p className="muted small">
                Rate each answer 1–5 below, then save the scorecard against the candidate's name.
                Unrated questions are excluded, so a partial interview still scores fairly.
              </p>
            )}
          </div>
        </section>
      )}

      {staged && inPlay.length === 0 && (
        <Empty title="No questions in this part yet">
          <p className="muted">
            Pick another part above, or add a question — it will belong to this one.
          </p>
        </Empty>
      )}

      <div className="stack">
        {grouped.map(([category, qs]) => (
          <section key={category} className="card">
            <div className="card-head">
              <h3>{category}</h3>
            </div>
            <div className="card-body">
              {qs.map((q) => (
                /* `.mini-row` is a flex ROW, so everything below — the question,
                   the guidance, the scale, the evidence box — was being laid
                   out as columns of one shared width. The question ended up a
                   few characters wide, one word per line, with the weight badge
                   and the buttons sitting on top of it. `.rec-q` turns the axis
                   back upright; these parts are a sequence, not a row. */
                <div key={q.RecruitingQuestionId} className="mini-row rec-q">
                  <div className="rec-q-head">
                    <div className="grow">
                      {q.CoreValue && (
                        <p className="xs mb-2">
                          <span className="badge badge-serious">{q.CoreValue}</span>{' '}
                          {meanings[q.CoreValue] && <span className="subtle">{meanings[q.CoreValue]}</span>}
                        </p>
                      )}
                      <p className="rec-q-text">{q.Text}</p>
                    </div>
                    {/* Kept on one line and out of the text's way: mid-interview
                        the weight matters and Edit/Delete do not, so they stay
                        quiet at the end of the row rather than beside the
                        question they were overlapping. */}
                    <div className="row rec-q-actions">
                      <span className={`badge ${WEIGHTS[q.Weight]?.badge ?? 'badge-neutral'}`}>
                        {WEIGHTS[q.Weight]?.label}
                      </span>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(q)}>
                        Edit
                      </button>
                      <DeleteButton
                        what="this question"
                        onConfirm={() => {
                          void onRun(() =>
                            api.del(`/people/recruiting/questions/${q.RecruitingQuestionId}`),
                          );
                        }}
                      />
                    </div>
                  </div>

                  {/* rec-signal, because both of these are written one signal
                      per line and HTML collapses newlines into spaces. Without
                      it "Sizes the environment immediately" and "Separates
                      personal ownership from vendor ownership" print as one
                      sentence — which is unreadable at the speed an
                      interviewer scans it while listening to an answer. */}
                  {q.Guidance && (
                    <p className="xs muted rec-q-guide rec-signal">
                      <strong>Strong answer: </strong>
                      {q.Guidance}
                    </p>
                  )}
                  {q.Note && <p className="risk-note xs rec-signal">{q.Note}</p>}

                  {scoring && (
                    <div className="rec-q-score">
                      <div className="scale" role="group" aria-label={`Rate: ${q.Text}`}>
                        {SCALE.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            title={s.blurb}
                            className={scores[q.RecruitingQuestionId] === s.value ? 'on' : ''}
                            aria-pressed={scores[q.RecruitingQuestionId] === s.value}
                            /* Clicking the selected value clears it, so a
                               mis-click can be undone back to "not rated" —
                               which is not the same as a 1. */
                            onClick={() =>
                              setScores((v) => {
                                const next = { ...v };
                                if (next[q.RecruitingQuestionId] === s.value) {
                                  delete next[q.RecruitingQuestionId];
                                } else {
                                  next[q.RecruitingQuestionId] = s.value;
                                }
                                return next;
                              })
                            }
                          >
                            {s.value}
                          </button>
                        ))}
                      </div>
                      {/* Always visible rather than behind a toggle nobody
                          presses mid-interview: a score with nothing attached
                          is the thing that falls apart in a debrief. */}
                      <textarea
                        className="textarea"
                        rows={2}
                        style={{ minHeight: 60 }}
                        value={comments[q.RecruitingQuestionId] ?? ''}
                        onChange={(e) =>
                          setComments((v) => ({ ...v, [q.RecruitingQuestionId]: e.target.value }))
                        }
                        placeholder="Evidence — a quote, a number, an example."
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {candidates.length > 0 && (
        <section className="card mt-4">
          <div className="card-head">
            <div className="grow">
              <h3>Saved scorecards</h3>
              <div className="sub">
                {candidates.length} for this role. The questions, weights and evidence are stored
                with each one, so editing the bank later does not change what an interview recorded.
              </div>
            </div>
          </div>

          {/* A staged process leaves several scorecards for one person, each
              covering a slice of the model, and none of them is the decision.
              Anybody with more than one gets a combined view — that is the
              document a debrief is actually run from. */}
          {competencies.length > 0 && byCandidate.length > 0 && (
            <div className="card-body tight">
              <span className="eyebrow">Ready to decide</span>
              <div className="stack" style={{ gap: 'var(--space-2)' }}>
                {byCandidate.map((g) => (
                  <div key={g.key} className="mini-row rec-q">
                    <div className="rec-q-head">
                      <div className="grow">
                        <span className="strong">{g.name}</span>
                        <div className="xs muted">
                          {g.cards.length} scorecards ·{' '}
                          {g.stages.length
                            ? g.stages.join(', ')
                            : 'no stage recorded'}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm rec-q-actions"
                        onClick={() => setCombining(g.name)}
                      >
                        Combined scorecard
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Verdict</th>
                  <th>Interviewer</th>
                  <th>Interviewed</th>
                  <th className="num">Rated</th>
                  <th className="actions" />
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => {
                  const band = bandFor(c.ScorePct);
                  return (
                    <tr key={c.CandidateId}>
                      <td className="strong">{c.Name}</td>
                      <td>
                        <span className={`badge ${band.badge}`}>
                          {c.ScorePct}% · {band.label}
                        </span>
                      </td>
                      <td>{c.Interviewer ?? '—'}</td>
                      <td>{formatDate(c.InterviewedOn ?? c.CreatedUtc)}</td>
                      <td className="num">
                        {c.RatedCount} / {c.QuestionCount}
                      </td>
                      <td className="actions">
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => setViewing(c.CandidateId)}
                        >
                          Open
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <div className="row wrap mt-4">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={busy}
          onClick={() => {
            if (
              !window.confirm(
                `Replace all ${questions.length} questions for ${role.Name} with the sample set? Your edits are lost.`,
              )
            ) {
              return;
            }
            void onRun(() =>
              api.post(`/people/recruiting/roles/${role.TrainingRoleId}/sample?replace=1`),
            );
          }}
        >
          Reset {role.Name} to the sample set
        </button>
        {role.CoreValueCount < role.CoreValuesTotal && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={() =>
              void onRun(() =>
                api.post(`/people/recruiting/roles/${role.TrainingRoleId}/core-values`),
              )
            }
          >
            Add the {role.CoreValuesTotal - role.CoreValueCount} missing core values{' '}
            {role.CoreValuesTotal - role.CoreValueCount === 1 ? 'question' : 'questions'}
          </button>
        )}
      </div>

      {showGuide && (
        <CompetencyGuide competencies={competencies} onClose={() => setShowGuide(false)} />
      )}

      {combining && (
        <CombinedDialog
          roleId={role.TrainingRoleId}
          roleName={role.Name}
          name={combining}
          competencies={competencies}
          onClose={() => setCombining(null)}
        />
      )}

      {(adding || editing) && (
        <QuestionDialog
          question={editing}
          roleId={role.TrainingRoleId}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onDone={() => {
            setAdding(false);
            setEditing(null);
            void onRun(async () => undefined);
          }}
        />
      )}

      {savingCard && result && (
        <SaveScorecardDialog
          role={role}
          result={result}
          scores={scores}
          comments={comments}
          onClose={() => setSavingCard(false)}
          onSaved={() => {
            setSavingCard(false);
            /* Clearing is the point: the next candidate starts from a blank
               scorecard rather than inheriting the previous one's ratings or
               the evidence written against them. */
            setScores({});
            setComments({});
            void onRun(async () => undefined);
          }}
        />
      )}

      {viewing !== null && (
        <ScorecardDialog
          candidateId={viewing}
          onClose={() => setViewing(null)}
          onDeleted={() => {
            setViewing(null);
            void onRun(async () => undefined);
          }}
        />
      )}
    </>
  );
}

/* =========================================================================
   The scorecard pieces
   ========================================================================= */

/**
 * What each competency scored and what it was worth.
 *
 * A single percentage hides the case that decides most hires — strong overall,
 * weak in the one place the role cannot carry a weakness.
 */
function ScoreBreakdown({ rows }: { rows: ScoreRow[] }) {
  const cats = breakdown(rows);
  if (cats.length === 0) return null;
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Competency</th>
            <th className="num">Weight</th>
            <th className="num">Score</th>
            <th className="num">Weighted</th>
          </tr>
        </thead>
        <tbody>
          {cats.map((c) => (
            <tr key={c.category}>
              <td>
                <span className="strong">{c.category}</span> <span className="xs subtle">· {c.n} rated</span>
              </td>
              <td className="num">{c.share}%</td>
              <td className={`num ${ratingClass(c.score)}`}>{c.score.toFixed(1)}</td>
              <td className="num">
                {c.got} / {c.max}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="xs muted mt-2">
        Weight is this competency's share of the points on offer, set by how many questions it has
        and how heavily they count. Score is the weighted mean on the same 1–5 scale you just rated
        on.
      </p>
    </div>
  );
}

/** Shown with every result rather than behind a link, because the band on its
 *  own reads as a verdict — the point is that it is a threshold someone chose. */
function DecisionThresholds({ pct }: { pct: number }) {
  const current = bandFor(pct);
  return (
    <div>
      <span className="eyebrow">Decision thresholds</span>
      <div className="table-wrap mt-2">
        <table className="table">
          <tbody>
            {BANDS.map((b, i) => (
              <tr key={b.label}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span className={`badge ${b === current ? b.badge : 'badge-neutral'}`}>
                    {bandRange(i)}
                  </span>
                </td>
                <td>
                  <span className={b === current ? 'strong' : 'muted'}>{b.label}</span>
                  <div className="xs subtle">{b.blurb}</div>
                </td>
                <td className="num">{b === current ? `← ${pct}%` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** A weak answer on a Critical question is what a good total hides, so it gets
 *  named next to the total rather than left for somebody to spot in the list.
 *  Not a veto — the debrief decides — but it has to be said. */
function CriticalConcerns({ rows }: { rows: ScoreRow[] }) {
  const low = criticalConcerns(rows);
  if (low.length === 0) return null;
  return (
    <div className="risk-note">
      <p className="small strong">
        {low.length} Critical question{low.length !== 1 ? 's' : ''} scored below 3
      </p>
      <ul className="xs muted mt-2">
        {low.map((r, i) => (
          <li key={i}>
            <strong>{r.rating}</strong> · {r.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* =========================================================================
   Dialogs
   ========================================================================= */

function QuestionDialog({
  question,
  roleId,
  onClose,
  onDone,
}: {
  question: RecruitingQuestion | null;
  roleId: number;
  onClose: () => void;
  onDone: () => void;
}) {
  const [category, setCategory] = useState(question?.Category ?? 'Technical');
  const [text, setText] = useState(question?.Text ?? '');
  const [guidance, setGuidance] = useState(question?.Guidance ?? '');
  const [note, setNote] = useState(question?.Note ?? '');
  const [weight, setWeight] = useState<number>(question?.Weight ?? 2);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      category: category.trim() || 'General',
      text: text.trim(),
      guidance: guidance.trim() || null,
      note: note.trim() || null,
      weight,
    };
    try {
      if (question) await api.put(`/people/recruiting/questions/${question.RecruitingQuestionId}`, payload);
      else await api.post('/people/recruiting/questions', { ...payload, trainingRoleId: roleId });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={question ? 'Edit question' : 'Add question'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !text.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Category">
          <input
            className="input"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g. Technical"
          />
        </Field>
        <Field label="Weight" hint="Critical counts triple a nice-to-have.">
          <select className="select" value={weight} onChange={(e) => setWeight(Number(e.target.value))}>
            {Object.entries(WEIGHTS).map(([value, w]) => (
              <option key={value} value={value}>
                {w.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Question">
        <textarea className="textarea" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
      </Field>
      <Field label="What a strong answer sounds like">
        <textarea
          className="textarea"
          rows={3}
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
          placeholder="Describes what earns a 5, so two interviewers score the same answer alike."
        />
      </Field>
      <Field
        label="Note for the interviewer"
        hint="Shown against the question for every candidate. Evidence about a particular candidate goes on the scorecard instead."
      >
        <textarea
          className="textarea"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="How to ask it — e.g. ask at stage 2 only, or follow up if they mention pricing."
        />
      </Field>
    </Modal>
  );
}

function SaveScorecardDialog({
  role,
  result,
  scores,
  comments,
  onClose,
  onSaved,
}: {
  role: RecruitingRole;
  result: {
    rated: number;
    total: number;
    got: number;
    max: number;
    pct: number;
    band: (typeof BANDS)[number];
    rows: ScoreRow[];
  };
  scores: Record<number, number>;
  comments: Record<number, string>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [interviewer, setInterviewer] = useState('');
  const [interviewedOn, setInterviewedOn] = useState(todayLocal());
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missingEvidence = Object.keys(scores).filter(
    (id) => !(comments[Number(id)] ?? '').trim(),
  ).length;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/people/recruiting/candidates', {
        trainingRoleId: role.TrainingRoleId,
        name: name.trim(),
        interviewer: interviewer.trim() || null,
        interviewedOn,
        notes: notes.trim() || null,
        scores,
        comments,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Save scorecard"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim()}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save scorecard'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="row wrap">
        <span className={`badge ${result.band.badge}`}>
          {result.pct}% · {result.band.label}
        </span>
        <span className="xs muted">
          {role.Name} · {result.got} of {result.max} weighted points · {result.rated} of{' '}
          {result.total} questions rated
        </span>
      </div>
      <p className="xs muted mt-2">{result.band.blurb}</p>

      <div className="mt-4">
        <CriticalConcerns rows={result.rows} />
      </div>

      <Field label="Candidate name">
        <input
          className="input"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Jordan Ellis"
        />
      </Field>

      <div className="form-grid">
        <Field label="Interviewer">
          <input className="input" value={interviewer} onChange={(e) => setInterviewer(e.target.value)} />
        </Field>
        <Field label="Interview date">
          <input
            className="input"
            type="date"
            value={interviewedOn}
            onChange={(e) => setInterviewedOn(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          className="textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Anything the ratings don't capture — strengths, concerns, next step."
        />
      </Field>

      {/* Not a blocker — an interviewer mid-debrief should not be stopped from
          saving — but worth naming, because a bare score is the thing that
          cannot be defended later. */}
      {missingEvidence > 0 && (
        <p className="xs muted">
          {missingEvidence === result.rated
            ? `No evidence recorded against ${
                result.rated === 1 ? 'the rated question' : `any of the ${result.rated} rated questions`
              }.`
            : `${missingEvidence} of the ${result.rated} rated questions have no evidence recorded.`}{' '}
          You can still save, but a score without a quote or a number is hard to defend in the
          debrief.
        </p>
      )}

      <p className="xs subtle mt-2">
        The questions, their weights and your evidence are stored with the scorecard, so editing the
        question bank later will not change what this interview recorded. Ratings are not editable
        afterwards for the same reason.
      </p>
    </Modal>
  );
}

function ScorecardDialog({
  candidateId,
  onClose,
  onDeleted,
}: {
  candidateId: number;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const candidate = useResource<CandidateDetail>(`/people/recruiting/candidates/${candidateId}`);
  const c = candidate.data;

  /* The model the printed copy is judged against. Fetched from the role the
     scorecard was taken for rather than passed in, because a scorecard is
     opened from a list that does not know it — and a role without one comes
     back empty, which is what makes the export fall back to the percentage. */
  const framework = useResource<RecruitingFramework>(
    c?.TrainingRoleId ? `/people/recruiting/roles/${c.TrainingRoleId}/framework` : null,
  );
  const model = useMemo(
    () =>
      (framework.data?.competencies ?? []).map((k) => ({
        ref: k.Ref,
        name: k.Name,
        weightPct: k.WeightPct,
        isGate: k.IsGate,
      })),
    [framework.data],
  );

  const rows: ScoreRow[] =
    c?.scores.map((s) => ({
      category: s.Category ?? 'Uncategorised',
      weight: s.Weight,
      rating: s.Rating,
      text: s.QuestionText,
    })) ?? [];

  return (
    <Modal
      title={c ? c.Name : 'Scorecard'}
      onClose={onClose}
      footer={
        c ? (
          <>
            <DeleteButton
              what={`the scorecard for ${c.Name}`}
              label="Delete scorecard"
              onConfirm={() => {
                void api.del(`/people/recruiting/candidates/${c.CandidateId}`).then(onDeleted);
              }}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => exportScorecardPdf(c, model)}
            >
              Print / save PDF
            </button>
            <button type="button" className="btn btn-ghost" onClick={onClose}>
              Close
            </button>
          </>
        ) : undefined
      }
    >
      {candidate.loading && <Loading label="Loading the scorecard" />}
      {candidate.error && <ErrorBanner message={candidate.error} />}

      {c && (
        <div className="stack">
          <div>
            <div className="row wrap">
              <span className={`badge ${bandFor(c.ScorePct).badge}`}>
                {c.ScorePct}% · {bandFor(c.ScorePct).label}
              </span>
              <span className="xs muted">
                {c.RoleName} · {c.ScoreTotal} of {c.ScoreMax} weighted points · {c.RatedCount} of{' '}
                {c.QuestionCount} questions rated
              </span>
            </div>
            <p className="xs subtle mt-2">
              Interviewer {c.Interviewer ?? '—'} · interviewed{' '}
              {formatDate(c.InterviewedOn ?? c.CreatedUtc)} · saved {formatDate(c.CreatedUtc)}
            </p>
          </div>

          {/* Built from the snapshot rather than the live bank, so a scorecard
              still breaks down the way it did on the day. */}
          <CriticalConcerns rows={rows} />
          <ScoreBreakdown rows={rows} />
          <DecisionThresholds pct={c.ScorePct} />

          {c.Notes && (
            <div>
              <span className="eyebrow">Interviewer notes</span>
              <p className="small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                {c.Notes}
              </p>
            </div>
          )}

          <div>
            <span className="eyebrow">Ratings and evidence</span>
            {c.scores.map((s) => (
              /* Same fix as the scoring list above: a rating, its question and
                 its evidence read down the page, not across it. */
              <div key={s.ScoreId} className="mini-row rec-q">
                <div className="row">
                  <span className={`mono ${ratingClass(s.Rating)}`}>{s.Rating}</span>
                  <span className="xs subtle">
                    {s.CoreValue ? `${s.CoreValue} · ` : ''}
                    {s.Category} · {WEIGHTS[s.Weight]?.label}
                  </span>
                </div>
                <p className="small">{s.QuestionText}</p>
                {s.Comment ? (
                  <p className="risk-note xs" style={{ whiteSpace: 'pre-wrap' }}>
                    {s.Comment}
                  </p>
                ) : (
                  <p className="xs subtle">No evidence recorded.</p>
                )}
              </div>
            ))}
          </div>

          <p className="xs subtle">
            The PDF opens your browser's print dialog — choose <strong>Save as PDF</strong> as the
            destination. It carries the competency breakdown, the thresholds and the evidence for
            each rating.
          </p>
        </div>
      )}
    </Modal>
  );
}
